/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Info, X, ShieldCheck, Article, Receipt, Lock, Warning as WarningIcon } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Bulle d'information « ⓘ » qui ouvre une fenêtre contenant tous les textes
 * juridiques de Auto-Gère : Mentions légales, CGU, CGV, Confidentialité, Risques.
 * Tous les contenus sont en dur (statiques) — pas de backend nécessaire.
 */
const LegalInfoDialog = ({ open: controlledOpen, onOpenChange, hideTrigger = false, compact = false }) => {
  const { language } = useLanguage();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const lang = language === 'en' ? 'en' : language === 'es' ? 'es' : 'fr';
  const today = new Date().toLocaleDateString(
    lang === 'en' ? 'en-US' : lang === 'es' ? 'es-ES' : 'fr-FR',
    { day: 'numeric', month: 'long', year: 'numeric' }
  );

  const LABELS = {
    fr: {
      tooltip: 'Mentions légales, CGU, CGV, confidentialité et avertissement de risque',
      btn: 'Voir les informations légales',
      title: 'Informations légales — Auto-Gère',
      desc: 'Tous les textes juridiques applicables à l\'utilisation du service Auto-Gère.',
      tabs: { legal: 'Mentions légales', cgu: 'CGU', cgv: 'CGV', privacy: 'Confidentialité', risk: 'Risques' },
      lastUpdate: 'Dernière mise à jour',
      close: 'Fermer'
    },
    en: {
      tooltip: 'Legal notice, Terms of Use, Terms of Sale, Privacy Policy and risk disclaimer',
      btn: 'View legal information',
      title: 'Legal information — Auto-Gère',
      desc: 'All legal texts applicable to the use of the Auto-Gère service.',
      tabs: { legal: 'Legal notice', cgu: 'Terms', cgv: 'Sale terms', privacy: 'Privacy', risk: 'Risk' },
      lastUpdate: 'Last updated',
      close: 'Close'
    },
    es: {
      tooltip: 'Aviso legal, CGU, CGV, política de privacidad y advertencia de riesgo',
      btn: 'Ver información legal',
      title: 'Información legal — Auto-Gère',
      desc: 'Todos los textos legales aplicables al uso del servicio Auto-Gère.',
      tabs: { legal: 'Aviso legal', cgu: 'CGU', cgv: 'CGV', privacy: 'Privacidad', risk: 'Riesgo' },
      lastUpdate: 'Última actualización',
      close: 'Cerrar'
    }
  };
  const L = LABELS[lang];

  // === Contenus juridiques ===
  // En français complet ; les EN/ES sont des résumés équivalents conservés
  // dans la même fenêtre via les onglets.
  const CONTENT_FR = {
    legal: (
      <>
        <h3 className="text-base font-bold text-black mb-2">1. Éditeur du service</h3>
        <p>Le service <strong>Auto-Gère</strong> (ci-après « le Service ») est édité et exploité à titre individuel par son propriétaire (ci-après « l'Éditeur »), domicilié au Canada. Toute correspondance peut être adressée à : <strong>support@autogere.ca</strong>.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">2. Hébergement</h3>
        <p>Le Service est hébergé sur l'infrastructure d'<strong>Emergent</strong> ; les données de l'utilisateur sont stockées dans une base MongoDB chiffrée. Les paiements récurrents sont opérés par <strong>PayPal</strong>. Les emails transactionnels sont envoyés via <strong>Resend</strong> depuis le domaine <code>autogere.ca</code>.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">3. Domaine</h3>
        <p>Le nom de domaine <code>autogere.ca</code> est la propriété exclusive de l'Éditeur.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">4. Propriété intellectuelle</h3>
        <p>L'ensemble des éléments composant le Service (code source, interfaces, logos, textes, illustrations, structure de données) est protégé par le droit d'auteur. Toute reproduction, représentation ou exploitation non autorisée est strictement interdite et passible de poursuites.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">5. Contact</h3>
        <p>Pour toute question : <strong>support@autogere.ca</strong>.</p>
      </>
    ),
    cgu: (
      <>
        <h3 className="text-base font-bold text-black mb-2">1. Objet</h3>
        <p>Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'utilisation du Service Auto-Gère. En créant un compte, l'utilisateur (ci-après « l'Utilisateur ») reconnaît avoir lu, compris et accepté l'intégralité des CGU.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">2. Description du Service</h3>
        <p>Auto-Gère est une application web permettant à l'Utilisateur de suivre ses transactions financières, de gérer un budget, de constituer un portefeuille d'actifs (actions et cryptomonnaies à titre informatif) et de générer des rapports. Le Service propose un plan gratuit limité (10 transactions, 4 actifs) et un plan Premium illimité.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">3. Création de compte</h3>
        <p>L'inscription nécessite une adresse email valide et un mot de passe robuste (8 caractères minimum, mixte). L'Utilisateur est seul responsable de la confidentialité de ses identifiants. Toute activité réalisée depuis un compte est réputée effectuée par son titulaire.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">4. Utilisation acceptable</h3>
        <p>L'Utilisateur s'engage à : (a) ne pas tenter d'accéder à des comptes tiers ; (b) ne pas effectuer d'opérations de rétro-ingénierie ; (c) ne pas utiliser le Service à des fins illégales ; (d) ne pas injecter de contenu malveillant. Tout manquement entraîne la résiliation immédiate sans remboursement.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">5. Disponibilité</h3>
        <p>L'Éditeur met en œuvre les meilleurs efforts pour assurer une disponibilité 24 h/24. Cependant, des interruptions pour maintenance ou incident peuvent survenir sans préavis. Aucune garantie de disponibilité contractuelle n'est offerte sur le plan gratuit.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">6. Limitation de responsabilité</h3>
        <p>Le Service est fourni « tel quel ». L'Éditeur ne saurait être tenu responsable des pertes financières, manques à gagner, ou dommages indirects résultant de l'utilisation ou de l'indisponibilité du Service. <strong>Auto-Gère n'est pas un conseiller financier agréé</strong> (voir onglet « Risques »).</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">7. Modification des CGU</h3>
        <p>Les CGU peuvent être modifiées à tout moment. L'Utilisateur est informé par email des modifications substantielles. La poursuite de l'utilisation après notification vaut acceptation.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">8. Droit applicable</h3>
        <p>Les CGU sont soumises au droit canadien. En cas de litige, les tribunaux du Québec sont compétents.</p>
      </>
    ),
    cgv: (
      <>
        <h3 className="text-base font-bold text-black mb-2">1. Objet</h3>
        <p>Les présentes Conditions Générales de Vente (« CGV ») régissent l'achat de l'abonnement <strong>Auto-Gère Premium</strong>.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">2. Plans et tarifs</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Premium mensuel</strong> : 9,99 CAD / mois (renouvellement automatique)</li>
          <li><strong>Premium annuel</strong> : 79,99 CAD / an (économie de 33 % vs mensuel)</li>
        </ul>
        <p className="mt-2">Les prix sont indiqués en dollars canadiens, toutes taxes comprises lorsque applicables.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">3. Paiement</h3>
        <p>Le paiement est traité exclusivement via <strong>PayPal</strong>. Auto-Gère ne collecte ni ne stocke aucune information de carte bancaire. Les facturations récurrentes sont autorisées par l'Utilisateur lors de la souscription.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">4. Renouvellement et résiliation</h3>
        <p>L'abonnement se renouvelle automatiquement à la fin de chaque période (mensuelle ou annuelle), sauf résiliation au préalable. L'Utilisateur peut résilier à tout moment depuis l'onglet « Paramètres → Abonnement ». La résiliation prend effet à la fin de la période en cours ; aucun remboursement prorata temporis n'est effectué.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">5. Droit de rétractation</h3>
        <p>Conformément à l'article L221-28 du Code de la consommation (et règles équivalentes au Canada/Québec), l'Utilisateur consent expressément à l'exécution immédiate du Service dès la souscription et <strong>renonce à son droit de rétractation</strong>.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">6. Remboursement</h3>
        <p>Aucun remboursement n'est garanti après le déblocage des fonctionnalités Premium. En cas de litige sérieux (défaut majeur de service), l'Utilisateur peut écrire à <strong>support@autogere.ca</strong> ; chaque cas est étudié individuellement.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">7. Suspension du compte</h3>
        <p>En cas de violation des CGU ou de défaut de paiement, l'accès Premium peut être suspendu sans préavis. L'Utilisateur conserve l'accès aux données de son compte gratuit.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">8. Facturation</h3>
        <p>Une facture PDF est envoyée automatiquement par email après chaque paiement réussi, au nom du compte enregistré sur PayPal.</p>
      </>
    ),
    privacy: (
      <>
        <h3 className="text-base font-bold text-black mb-2">1. Données collectées</h3>
        <p>Auto-Gère collecte uniquement les données nécessaires à la fourniture du Service :</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Compte</strong> : email, nom complet, mot de passe (haché bcrypt), tier d'abonnement, statut 2FA</li>
          <li><strong>Données financières saisies</strong> : transactions, budgets, portefeuille d'actifs (entièrement saisies par l'Utilisateur — aucune synchronisation bancaire automatique)</li>
          <li><strong>Données techniques</strong> : adresse IP de connexion, dates d'accès, navigateur</li>
          <li><strong>Paiement</strong> : identifiant d'abonnement PayPal uniquement (aucune donnée de carte bancaire)</li>
        </ul>

        <h3 className="text-base font-bold text-black mt-4 mb-2">2. Finalités</h3>
        <p>Les données servent exclusivement à : (a) fournir les fonctionnalités du Service ; (b) sécuriser le compte (anti-fraude, 2FA) ; (c) facturer l'abonnement Premium ; (d) envoyer les alertes et notifications souscrites par l'Utilisateur.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">3. Partage avec des tiers</h3>
        <p>Aucune donnée n'est vendue, louée ou partagée à des fins marketing. Les seuls partenaires techniques avec accès limité aux données strictement nécessaires sont :</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>PayPal</strong> (traitement des paiements)</li>
          <li><strong>Resend</strong> (envoi des emails transactionnels)</li>
          <li><strong>Yahoo Finance / yfinance</strong> (cours des actifs — aucune donnée personnelle envoyée)</li>
          <li><strong>Google Gemini</strong> (analyse OCR des factures scannées par l'Utilisateur — images supprimées après traitement)</li>
        </ul>

        <h3 className="text-base font-bold text-black mt-4 mb-2">4. Durée de conservation</h3>
        <p>Les données du compte sont conservées tant que le compte est actif. <strong>L'Utilisateur peut supprimer définitivement son compte à tout moment</strong> via Paramètres → Zone dangereuse. Suite à la suppression, l'ensemble des données est effacé sous 30 jours. Les archives PDF sont automatiquement supprimées après une rotation de 7 ans (obligations fiscales).</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">5. Vos droits (RGPD / Loi 25 Québec)</h3>
        <p>Vous disposez des droits suivants :</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Accès</strong> : exportez vos données en PDF, Excel ou JSON depuis Rapports</li>
          <li><strong>Rectification</strong> : modifiez vos informations dans Paramètres</li>
          <li><strong>Suppression</strong> : Paramètres → Zone dangereuse → Supprimer mon compte</li>
          <li><strong>Portabilité</strong> : export complet via Rapports → Export rapide</li>
          <li><strong>Opposition / limitation</strong> : écrire à <strong>support@autogere.ca</strong></li>
        </ul>

        <h3 className="text-base font-bold text-black mt-4 mb-2">6. Sécurité</h3>
        <p>Les mots de passe sont hachés avec bcrypt. Les communications utilisent HTTPS/TLS. L'authentification à deux facteurs (2FA) est disponible gratuitement. Les sauvegardes de la base sont chiffrées au repos.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">7. Cookies</h3>
        <p>Auto-Gère utilise uniquement des cookies techniques nécessaires au fonctionnement (session, préférence de langue, thème). Aucun cookie de tracking publicitaire n'est déposé.</p>
      </>
    ),
    risk: (
      <>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded text-red-900 mb-4">
          <p className="font-bold text-base">⚠️ AVERTISSEMENT IMPORTANT</p>
          <p className="mt-1 text-sm">
            Auto-Gère est un outil personnel de suivi budgétaire et de portefeuille à <strong>vocation informative uniquement</strong>.
            <strong> Auto-Gère n'est ni un conseiller financier agréé, ni un courtier, ni un fonds d'investissement.</strong>
          </p>
        </div>

        <h3 className="text-base font-bold text-black mb-2">1. Absence de conseil en investissement</h3>
        <p>Aucune information, donnée, projection ou suggestion fournie par Auto-Gère ne constitue un conseil en investissement, une recommandation d'achat/vente ou une sollicitation. Les cours des actions et cryptomonnaies affichés sont fournis à titre indicatif via des sources publiques (Yahoo Finance) et peuvent comporter un délai ou des erreurs.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">2. Risque de perte en capital</h3>
        <p>Les investissements en bourse et en cryptomonnaies présentent un risque élevé de <strong>perte totale du capital investi</strong>. Les performances passées ne préjugent pas des performances futures. Avant toute décision d'investissement, l'Utilisateur s'engage à consulter un professionnel agréé (conseiller financier, fiscaliste, comptable).</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">3. Cryptomonnaies</h3>
        <p>Les cryptomonnaies sont des actifs hautement volatils, non régulés dans certaines juridictions, et peuvent perdre 50 % de leur valeur en quelques heures. Leur fiscalité est complexe et varie selon les pays.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">4. Précision des données</h3>
        <p>Les données financières (cours, dividendes, calculs de rendement) sont récupérées via des API tierces. Auto-Gère ne garantit ni l'exactitude, ni l'exhaustivité, ni la disponibilité de ces données. L'Utilisateur doit toujours vérifier auprès de sa banque, de son courtier ou de sa plateforme officielle avant toute décision.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">5. Décharge de responsabilité</h3>
        <p>En utilisant Auto-Gère, l'Utilisateur reconnaît expressément que toute décision financière prise sur la base des informations affichées dans l'application relève de sa <strong>seule et entière responsabilité</strong>. L'Éditeur ne pourra en aucun cas être tenu responsable de pertes, manques à gagner, ou conséquences fiscales découlant de ces décisions.</p>

        <h3 className="text-base font-bold text-black mt-4 mb-2">6. Recommandation</h3>
        <p>Pour toute opération significative (placement &gt; 10 000 $, optimisation REER/CELI, succession, immobilier), Auto-Gère recommande vivement de consulter <strong>un conseiller financier agréé par l'AMF (Québec) ou les organismes équivalents</strong>.</p>
      </>
    )
  };

  // Mode EN / ES = on affiche le contenu FR avec une note. Pour un MVP français,
  // c'est suffisant ; la traduction complète peut être ajoutée plus tard.
  const ENGLISH_HEADER = (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-4">
      The legal documents are currently authoritative in <strong>French only</strong> (under Quebec law). An English translation is informational. For any legal question, the French version prevails.
    </div>
  );
  const SPANISH_HEADER = (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-4">
      Los documentos legales son oficiales únicamente en <strong>francés</strong> (derecho de Quebec). La traducción al español es informativa. En caso de duda, prevalece la versión francesa.
    </div>
  );

  const wrap = (key) => (
    <div className="space-y-3 text-sm text-slate-800 leading-relaxed">
      {lang === 'en' && ENGLISH_HEADER}
      {lang === 'es' && SPANISH_HEADER}
      {CONTENT_FR[key]}
    </div>
  );

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          data-testid="legal-info-btn"
          onClick={() => setOpen(true)}
          title={L.tooltip}
          aria-label={L.btn}
          className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:underline text-sm font-semibold transition-colors group"
        >
          <span className={`shrink-0 inline-flex items-center justify-center rounded-full bg-blue-100 border border-blue-300 group-hover:bg-blue-200 group-hover:border-blue-500 transition-colors ${compact ? 'w-7 h-7' : 'w-10 h-10'}`}>
            <Info size={compact ? 16 : 24} weight="duotone" className="text-blue-700" />
          </span>
          <span className={compact ? 'text-xs' : 'text-base'}>{L.btn}</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="legal-info-dialog"
          className="bg-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-black flex items-center gap-2">
              <ShieldCheck size={22} weight="duotone" className="text-blue-700" />
              {L.title}
            </DialogTitle>
            <DialogDescription className="text-slate-700">
              {L.desc} · <span className="text-xs">{L.lastUpdate} : {today}</span>
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="legal" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full grid grid-cols-5 bg-blue-50 border border-blue-200 h-auto shrink-0">
              <TabsTrigger value="legal" data-testid="legal-tab-legal" className="data-[state=active]:bg-white data-[state=active]:text-blue-800 text-xs sm:text-sm flex items-center gap-1.5 py-2">
                <Article size={14} weight="duotone" /> <span className="hidden sm:inline">{L.tabs.legal}</span>
              </TabsTrigger>
              <TabsTrigger value="cgu" data-testid="legal-tab-cgu" className="data-[state=active]:bg-white data-[state=active]:text-blue-800 text-xs sm:text-sm flex items-center gap-1.5 py-2">
                <Article size={14} weight="duotone" /> {L.tabs.cgu}
              </TabsTrigger>
              <TabsTrigger value="cgv" data-testid="legal-tab-cgv" className="data-[state=active]:bg-white data-[state=active]:text-blue-800 text-xs sm:text-sm flex items-center gap-1.5 py-2">
                <Receipt size={14} weight="duotone" /> {L.tabs.cgv}
              </TabsTrigger>
              <TabsTrigger value="privacy" data-testid="legal-tab-privacy" className="data-[state=active]:bg-white data-[state=active]:text-blue-800 text-xs sm:text-sm flex items-center gap-1.5 py-2">
                <Lock size={14} weight="duotone" /> {L.tabs.privacy}
              </TabsTrigger>
              <TabsTrigger value="risk" data-testid="legal-tab-risk" className="data-[state=active]:bg-white data-[state=active]:text-red-700 text-xs sm:text-sm flex items-center gap-1.5 py-2">
                <WarningIcon size={14} weight="duotone" /> {L.tabs.risk}
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto pr-2 mt-4">
              <TabsContent value="legal" data-testid="legal-content-legal">{wrap('legal')}</TabsContent>
              <TabsContent value="cgu" data-testid="legal-content-cgu">{wrap('cgu')}</TabsContent>
              <TabsContent value="cgv" data-testid="legal-content-cgv">{wrap('cgv')}</TabsContent>
              <TabsContent value="privacy" data-testid="legal-content-privacy">{wrap('privacy')}</TabsContent>
              <TabsContent value="risk" data-testid="legal-content-risk">{wrap('risk')}</TabsContent>
            </div>
          </Tabs>

          <div className="shrink-0 pt-3 border-t border-slate-200 flex justify-end">
            <button
              type="button"
              data-testid="legal-close-btn"
              onClick={() => setOpen(false)}
              className="px-4 py-2 bg-white text-black border-2 border-blue-600 hover:bg-blue-50 rounded-md font-semibold text-sm inline-flex items-center gap-2"
            >
              <X size={14} weight="bold" /> {L.close}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LegalInfoDialog;
